import React from "react";
import { Action, actions, ActionThunk, LocalForm as RFFLocalForm, LocalFormProps } from "react-redux-form";

interface Props<T> extends Omit<LocalFormProps, "initialState"> {
  /**
   * Initial state of the form.
   * 
   * N.B. `LocalForm` is an uncontrolled component, i.e. if the value of `initialState` changes while `LocalForm` is mounted then there will be no effect.
   */
  initialState: T;

  /**
   * A render function that receives the current model state, and an update model callback.
   */
  children: (model: T, updateModel: (updates: Record<string, unknown>) => void) => JSX.Element;
}

// TODO this isn't quite right, Redux's Dispatch<T> doesn't return a Promise
type DispatchFn = <T>(action: Action | ActionThunk) => Promise<T>;

// TODO this will cause issues when there are multiple instances on one page!
let formDispatch: DispatchFn;

// Holds state to pass values between a RFF LocalForm and non-form elements in the same UI

/**
 * A wrapper around `react-redux-form`'s `LocalForm` component. When mixing RRF- and non-RRF form inputs its convenient to use this component as it provides easy access to the current form state.
 * 
 * When writing new forms, you should prefer this component over either the `Form` or `LocalForm` components in `react-redux-form`.
 * 
 * @status stable
 */
export function LocalForm<T extends {}>({ children, ...rrfProps }: Props<T>) {
  const [state, setState] = React.useState<T>({
    ...rrfProps.initialState,
  });

  const attachDispatch = (dispatch: DispatchFn) => {
    formDispatch = dispatch;
  };

  // Update values on the RFF model by dispatching actions
  const updateModel = (updates: Record<string, unknown>) => {
    const changes = Object.entries(updates).map(([model, value]) => {
      if (model.startsWith(".")) {
        // Shorthand .model doesn't work when dispatching
        model = `${rrfProps.model ?? "local"}${model}`;
      }

      return actions.change(model, value);
    })

    for (const action of changes) {
      formDispatch(action);
    }
  };

  // Merges RRF model into the non-RRF component state to be used by non-RRF components
  const updateState = (update: Partial<T>) => {
    setState({
      ...state,
      ...update,
    });
  };

  return (
    <RFFLocalForm {...rrfProps}
      initialState={state}
      onChange={updateState}
      getDispatch={(dispatch) => attachDispatch(dispatch as DispatchFn)}
    >
      {children?.(state, updateModel)}
    </RFFLocalForm>
  );
}
